import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

const RECOVERY_KEY_PREFIX = 'deeptrade:lazy-chunk-recovery:';

export const isLazyChunkLoadError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return /failed to fetch dynamically imported module|importing a module script failed|loading chunk .* failed/i.test(message);
};

export async function loadWithChunkRecovery<T>(
  loader: () => Promise<T>,
  recoveryKey: string,
  reload: () => void = () => window.location.reload(),
): Promise<T> {
  const storageKey = `${RECOVERY_KEY_PREFIX}${recoveryKey}`;

  try {
    const module = await loader();
    window.sessionStorage.removeItem(storageKey);
    return module;
  } catch (error) {
    if (!isLazyChunkLoadError(error)) {
      throw error;
    }

    if (!window.sessionStorage.getItem(storageKey)) {
      window.sessionStorage.setItem(storageKey, '1');
      reload();
    } else {
      window.sessionStorage.removeItem(storageKey);
    }

    throw error;
  }
}

export function lazyWithChunkRecovery<T extends ComponentType<any>>(
  loader: () => Promise<{ default: T }>,
  recoveryKey: string,
): LazyExoticComponent<T> {
  return lazy(() => loadWithChunkRecovery(loader, recoveryKey));
}
